import { PlaybackMode } from '@lucimark/shared/database/custom-types/playback-mode'

export interface CampaignFrameSequenceAssignment {
  playlistUrn: string
  frameCount: number
  playWithoutInterruption: boolean
}

export interface CampaignFrameSequenceRef { urn: string, frameIndex: number }

function buildRotatingRefs(assignments: CampaignFrameSequenceAssignment[]): CampaignFrameSequenceRef[] {
  if (assignments.length === 0) {
    return []
  }
  const meta: Record<string, number> = {}
  for (const ap of assignments) {
    meta[ap.playlistUrn] = ap.frameCount
  }
  const seq: CampaignFrameSequenceRef[] = []
  const pushFrames = (urn: string, from: number, to: number) => {
    for (let f = from; f < to; f++) {
      seq.push({ urn, frameIndex: f })
    }
  }
  for (const ap of assignments) {
    const n = meta[ap.playlistUrn] ?? 0
    pushFrames(ap.playlistUrn, 0, n)
  }
  return seq
}

function fisherYatesShuffle<T>(items: T[], random: () => number): T[] {
  const a = [...items]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    const t = a[i]!
    a[i] = a[j]!
    a[j] = t
  }
  return a
}

/**
 * Builds the ordered sequence of { urn, frameIndex } for a schedule root from playback mode and assignments.
 * Shared by loop duration, app preview, and player queue resolution so ordering stays aligned.
 *
 * {@link PlaybackMode.Random} returns a shuffled copy (use {@link resolvePlaybackSegments} with
 * `scheduleRootRandomRefOrderCache` in the player so the order stays stable across periodic rebuilds).
 */
export function buildFrameSequenceRefs(
  playbackMode: PlaybackMode,
  assignments: CampaignFrameSequenceAssignment[]
): CampaignFrameSequenceRef[] {
  const rotating = buildRotatingRefs(assignments)
  const mode = playbackMode ?? PlaybackMode.Rotating
  if (mode === PlaybackMode.Random) {
    return fisherYatesShuffle(rotating, Math.random)
  }
  return rotating
}
